// Anonyme Nutzungsmessung (02.11.2026, Konzept vom Betreiber freigegeben).
//
// Die Oberfläche meldet zehn Ereignisse, gesammelt und gebündelt. Was in
// eine Meldung darf, entscheidet der Server (api/lib/nutzung.php) — er
// nimmt nur die zehn bekannten Ereignisse an. Hier gilt dieselbe Zusage:
//
//   - KEINE PERSON. Kein Name, keine Kennung, keine Sitzung geht mit. Nur
//     Ereignis, Ort, Zahl, Gerätetyp.
//   - KEINE KETTE. Zeiten misst dieses Modul selbst und schickt nur die
//     fertige Zahl, nie eine Abfolge von Klicks.
//   - KEINE EINGABEN, außer dem Suchbegriff bei „ohne Treffer".
//
// In den Tests ist die Messung aus, damit sie keine Abrufe in fremde
// Attrappen streut; `nutzungFuerTests()` schaltet sie gezielt ein. In der
// Schulungsumgebung ist sie ebenfalls aus — Übungsklicks sind kein Alltag.

import { istSchulung } from '@/components/layout/SchulungsBand';

export type NutzungEreignis =
  | 'suche_ohne_treffer'
  | 'zeit_bis_bestellung'
  | 'sofort_zurueck'
  | 'dialog_abgebrochen'
  | 'warnung_spaeter'
  | 'fehler_gezeigt'
  | 'knopf'
  | 'erfassung_dauer'
  | 'seite'
  | 'ladezeit';

export type NutzungGeraet = 'pc' | 'tablet' | 'handy';

type Meldung = {
  ereignis: NutzungEreignis;
  geraet: NutzungGeraet;
  ort?: string;
  wert?: number;
  text?: string;
};

const SAMMELN_MS = 5000;
const HOECHSTENS = 50;

let aktiv = import.meta.env.MODE !== 'test';
let warteschlange: Meldung[] = [];
let zeitgeber: ReturnType<typeof setTimeout> | null = null;

/** Nur für Tests: Messung an, Warteschlange leer, Sendefunktion abfangbar. */
export function nutzungFuerTests(an: boolean): void {
  aktiv = an;
  warteschlange = [];
  if (zeitgeber !== null) clearTimeout(zeitgeber);
  zeitgeber = null;
  suchStart = null;
}

/** Nur für Tests: was gerade auf das Senden wartet. */
export function nutzungWarteschlange(): readonly Meldung[] {
  return warteschlange;
}

/** Gerätetyp aus Zeiger und Breite — ohne jede Kennung des Geräts. */
export function geraetTyp(
  breite: number = window.innerWidth,
  grob: boolean = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
): NutzungGeraet {
  if (breite < 600) return 'handy';
  return grob ? 'tablet' : 'pc';
}

function messungLaeuft(): boolean {
  if (!aktiv) return false;
  try {
    return !istSchulung();
  } catch {
    return false;
  }
}

/** Eine Meldung vormerken. Gesendet wird gebündelt nach fünf Sekunden. */
export function melde(
  ereignis: NutzungEreignis,
  zusatz: { ort?: string; wert?: number; text?: string } = {}
): void {
  if (!messungLaeuft()) return;
  const m: Meldung = { ereignis, geraet: geraetTyp() };
  if (zusatz.ort) m.ort = zusatz.ort;
  if (zusatz.wert !== undefined && Number.isFinite(zusatz.wert)) m.wert = Math.max(0, Math.round(zusatz.wert));
  if (zusatz.text && ereignis === 'suche_ohne_treffer') m.text = zusatz.text.slice(0, 100);
  warteschlange.push(m);
  if (warteschlange.length >= HOECHSTENS) {
    senden();
    return;
  }
  if (zeitgeber === null) zeitgeber = setTimeout(senden, SAMMELN_MS);
}

/** Sendet, was wartet. Fehler werden verschluckt — Messung ist nie wichtiger als Arbeit. */
export function senden(): void {
  if (zeitgeber !== null) clearTimeout(zeitgeber);
  zeitgeber = null;
  if (warteschlange.length === 0) return;
  const ereignisse = warteschlange.splice(0, HOECHSTENS);
  try {
    void fetch('/api/nutzung/melden.php', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ereignisse })
    }).catch(() => undefined);
  } catch {
    // nichts — siehe oben
  }
}

if (typeof document !== 'undefined') {
  // Beim Verlassen der Seite oder Wegschalten der App: sofort senden, sonst
  // ginge der letzte Rest verloren.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') senden();
  });
  window.addEventListener('pagehide', senden);
}

// ---------------------------------------------------------------------
// Zeit bis zur richtigen Bestellung: vom ersten Tippen im Suchfeld des
// Dashboards bis zum Öffnen einer Bestellung. Gemessen hier, gesendet
// wird nur die Zahl.
// ---------------------------------------------------------------------

let suchStart: number | null = null;

/** Erstes Tippen nach dem letzten Öffnen: Uhr starten. Leeren: Uhr aus. */
export function sucheBegonnen(begriff: string): void {
  if (begriff.trim() === '') {
    suchStart = null;
    return;
  }
  if (suchStart === null) suchStart = performance.now();
}

/** Eine Bestellung wurde aus dem Dashboard geöffnet. */
export function bestellungGeoeffnet(): void {
  if (suchStart !== null) {
    melde('zeit_bis_bestellung', { ort: 'dashboard', wert: performance.now() - suchStart });
  }
  suchStart = null;
}

// ---------------------------------------------------------------------
// Seiten: der Pfad ohne Bestellnummer — „/bestellungen/B-2026-0001" wird
// „bestellung". Eine Nummer würde einen Vorgang benennen.
// ---------------------------------------------------------------------

export function seitenName(pfad: string): string {
  const p = pfad.replace(/\/+$/, '') || '/';
  if (p === '/') return 'dashboard';
  if (/^\/bestellungen\/[^/]+\/druck$/.test(p)) return 'zettel';
  if (/^\/bestellungen\/[^/]+\/lieferschein$/.test(p)) return 'lieferschein';
  if (/^\/bestellungen\/[^/]+$/.test(p)) return 'bestellung';
  if (p.startsWith('/drucken/')) return 'drucken';
  return p.slice(1).split('/')[0] || 'dashboard';
}

/**
 * Fehlermeldung ohne Inhalt: Was in Anführungszeichen steht (Titel,
 * Namen) und Ziffern (Nummern, ISBN) fliegt raus, bevor gemeldet wird.
 */
export function fehlerOhneInhalt(text: string): string {
  return text
    .replace(/[„"“»«][^„"“”»«]*[“”"«»]/g, '…')
    .replace(/\d+/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60);
}

/**
 * Knöpfe, deren Nutzung gezählt wird: `data-nutzung="<code>"` am Knopf.
 * Die Liste der Codes steht im Server (NUTZUNG_KNOEPFE), damit die
 * Auswertung auch die nie benutzten nennen kann. Ein globaler Zuhörer
 * genügt — kein Knopf muss dafür seine Logik ändern.
 */
if (typeof document !== 'undefined') {
  document.addEventListener(
    'click',
    (e) => {
      const ziel = e.target instanceof Element ? e.target.closest('[data-nutzung]') : null;
      const code = ziel?.getAttribute('data-nutzung');
      if (code) melde('knopf', { ort: code });
    },
    true
  );
}
